/**
 * api/hazard.js — FR-04 Аюулын мэдээллийн web API.
 *
 * Backend нь mobile болон web-д НЭГ endpoint (`/api/mobile/hazard`) ашигладаг.
 * Web-ээс илгээхдээ `source: "web"` гэж тэмдэглэнэ — backend бүртгэлийг
 * mobile/web-ээр нь `source` баганаар ялгана.
 */
import { API } from "src/api/request";

// ---- Лавлахууд ----
export async function getCategories() {
  const res = await API().get("/api/mobile/hazard/categories");
  return res.data;
}

export async function getSeverities() {
  const res = await API().get("/api/mobile/hazard/severities");
  return res.data;
}

// ---- Жагсаалт ----
export async function getHazards(params) {
  const res = await API().get("/api/mobile/hazard", { params: { ...params } });
  return res.data;
}

export async function getHazardById(id) {
  const res = await API().get("/api/mobile/hazard/" + id);
  return res.data;
}

// ---- Үүсгэх (web) ----
export async function createHazard(params) {
  /* ⚠ БҮХ ТАЛБАРЫГ ДАМЖУУЛНА (2026-09-19).
       Урьд нь энд тогтсон цагаан жагсаалт байсан тул зөрчлийн маягтын
       талбарууд — мэдээллийн ангилал, хөдөлмөрийн нөхцөл, эрсдэлтэй эсэх,
       зөрчил гаргагч, зөрчлийн төрөл/заалт/шийтгэл, арга хэмжээ, хариуцагч
       БН, гүйцэтгэх хугацаа, төлөвлөсөн арга хэмжээ, бүртгэгдсэн огноо,
       page_kind — ҮҮСГЭХ үед ЧИМЭЭГҮЙ хаягддаг байв (ЗАСАХ үед бүгд явдаг
       байсан). Backend `_apply_vclass` зөв ажиллаж байхад жагсаалтад юу ч
       харагддаггүйн шалтгаан энэ байв. Backend нь өөрт хэрэггүй түлхүүрийг
       үл тоомсорлодог тул шүүх шаардлагагүй. */
  const res = await API().post("/api/mobile/hazard", {
    ...params,
    location_text: params.location_text,
    gps_lat: params.gps_lat,
    gps_lng: params.gps_lng,
    gps_accuracy: params.gps_accuracy,
    workplace_id: params.workplace_id,
    hazard_category_id: params.hazard_category_id,
    hazard_category_ids: params.hazard_category_ids,
    hazard_id: params.hazard_id,
    description: params.description,
    voice_transcript: params.voice_transcript,
    voice_file_url: params.voice_file_url,
    severity: params.severity,
    affected_location: params.affected_location,
    affected_people_count: params.affected_people_count,
    self_in_danger: params.self_in_danger,
    // Аюул vs зөрчлийг ялгах горим + үзлэгийн мета (backend report_number
    // prefix болон жагсаалтын шүүлтэнд ашиглана)
    report_mode: params.report_mode,
    inspection_level: params.inspection_level,
    inspection_type: params.inspection_type,
    inspection_type_id: params.inspection_type_id,
    // Объектын ангилал — журам 2.7.5 (зорилтот хяналт)
    inspection_object_id: params.inspection_object_id,
    photos: params.photos || [],
    source: "web",
    offline_client_id: params.offline_client_id,
    // 5А/15 асуумжаас илэрсэн аюулыг ажилтны бүртгэлтэй холбоно (hazard_reported).
    // web prework нь meta дотор, mobile нь шууд oea_registration_id-ээр илгээнэ.
    meta: params.meta,
    oea_registration_id: params.oea_registration_id,
  });
  return res.data;
}

export async function updateHazard(id, params) {
  const res = await API().put("/api/mobile/hazard/" + id, { ...params });
  return res.data;
}

// Логик устгал (soft delete) — эрхийн матрицаар backend шалгана
export async function deleteHazard(id) {
  const res = await API().delete("/api/mobile/hazard/" + id);
  return res.data;
}

// ---- Workflow үйлдлүүд ----
/**
 * Аюулын мэдээллийг хариуцагчид хуваарилна.
 *
 * ⚠ `dueDate` — «YYYY-MM-DD». Өгвөл ноцтой байдлаас автоматаар бодогдсон
 *   SLA-г ДАРНА: хуваарилагч бодит нөхцөлийг (сэлбэг ирэх, засварын
 *   хуваарь) мэддэг тул түүний тавьсан хугацаа давуу.
 *
 * ⚠ Эрхийг сервер тал шалгана — зөвхөн ХАБ хэлтсийн мэргэжилтэн.
 */
export async function assignHazard(id, assigneeTn, note, dueDate, workType) {
  const res = await API().post("/api/mobile/hazard/" + id + "/assign", {
    assignee_tn: assigneeTn,
    note,
    due_date: dueDate || undefined,
    // "OEA" | "NARYAD" — хариуцагчийн бүртгэлийн хуудсанд «ирсэн ажил»
    // болж гарна. Хоосон бол зөвхөн «Миний даалгавар» дээр харагдана.
    work_type: workType || undefined,
  });
  return res.data;
}

/**
 * НЭГЖИД хуваарилна — хүн рүү БИШ (2026-10-28).
 *
 * ⚠ ХОЁР ШАТЛАЛТ урсгалын ЭХНИЙ шат:
 *     1) ХАБ хэлтэс → НЭГЖИД (энэ дуудлага)
 *     2) тэр нэгжийн ХАБЭА инженер → нэгж дотроос ХҮНД (`assignHazard`)
 *
 * ⚠ Хэрэглэгчийн гомдлыг шийдэж байна: «Товчоон дарга руу нь
 *   хуваарилахад инженерүүдэд нь харагдахгүй, олон хуваарилах үйлдэл
 *   давтагдах гээд байна». Нэгжид хуваарилахад тэр нэгжийн БҮХ ХАБЭА
 *   инженер (бүлэг 179) мөрийг харна.
 *
 * ⚠ ТӨЛӨВИЙГ ХӨНДӨХГҮЙ — эзэн нь хараахан тодороогүй тул мөр нь
 *   нэгжийн «хүлээгдэж буй» дараалалд үлдэнэ.
 */
export async function assignHazardUnit(id, unitCode, unitName, note) {
  const res = await API().post("/api/mobile/hazard/" + id + "/assign", {
    unit_code: unitCode,
    unit_name: unitName || undefined,
    note: note || undefined,
  });
  return res.data;
}

/**
 * Үүсгэсэн наряд/ӨЭА-г аюулын мэдээлэлтэй холбоно.
 *
 * ⚠ Энэ дуудлагын ДАРАА л мөр «ирсэн ажил» жагсаалтаас хасагдана. Ажил
 *   үүсгээд холбоогүй бол банер үлдэж, хоёр дахь наряд үүсэх эрсдэлтэй.
 */
export async function linkHazardWork(id, jobId) {
  const res = await API().post("/api/mobile/hazard/" + id + "/link-work", {
    job_id: jobId,
  });
  return res.data;
}

/** Ангилалтын хураангуй — «Шинэ %» нь ахицыг хэмжих гол тоо. */
export async function getTriageSummary(days) {
  const res = await API().get("/api/mobile/hazard/triage-summary", {
    params: days ? { days } : {},
  });
  return res.data;
}

export async function completeHazard(id, responseAction, afterPhotos) {
  const res = await API().post("/api/mobile/hazard/" + id + "/complete", {
    response_action: responseAction,
    after_photos: afterPhotos || [],
  });
  return res.data;
}

export async function rejectHazard(id, reason) {
  const res = await API().post("/api/mobile/hazard/" + id + "/reject", {
    reason,
  });
  return res.data;
}

// ============================================================================
// ЗӨРЧИЛ — ХЯНАЛТ ШАЛГАЛТЫН МӨРӨӨС (2026-09-29)
//
// Зөрчил нь ОДОО `xab.inspection_item_result`-д бүртгэгддэг тул дээрх
// аюулын endpoint-ууд түүнд ТОХИРОХГҮЙ.
//
// ⚠ `getHazardById(id)`-ЫГ ЗӨРЧИЛД ХЭРЭГЛЭЖ БОЛОХГҮЙ: `id` нь
//   `inspection_item_result.id` бөгөөд тэр endpoint нь
//   `xab.hazard_reports`-ыг ЯГ ТЭР id-аар уншдаг. Хоёр хүснэгтийн id
//   1-ээс тоологддог тул ӨӨР ХҮНИЙ аюулын бүртгэл гарч ирнэ.
// ============================================================================
export async function getViolationById(id) {
  const res = await API().get("/xab/violation-detail/" + id);
  return res.data;
}

/** Зөрчлийг шийдвэрлэсэн (reject=true бол татгалзсан) гэж тэмдэглэх. */
export async function closeViolation(id, note, reject = false) {
  const res = await API().post("/xab/violation-detail/" + id, {
    action: note,
    reject,
  });
  return res.data;
}

// ============================================================================
// АЖИЛТАН ӨӨРӨӨ ШИЙДЭХ + УДИРДЛАГЫН БАТАЛГААЖУУЛАЛТ
//
// Урсгал: нээлттэй самбараас сонгоно → `selfFixHazard` (SELF_FIXED) →
//         удирдлага `verifyHazardFix` (COMPLETED эсвэл TRIAGE рүү буцаана).
//
// ⚠ Нээлттэй самбар нь үндсэн жагсаалтаас ЯЛГААТАЙ: удирдлагын түвшний
//   шүүлтгүй, үйлдвэр даяарх ШИЙДЭГДЭЭГҮЙ бүртгэлийг өгнө. Тиймээс
//   `getHazards`-ыг ашиглаж болохгүй — тэр нь эрхээр хумигдана.
// ============================================================================

/** Нээлттэй (шийдэгдээгүй) бүртгэлүүд — үйлдвэр даяар. */
export async function getOpenBoard(params = {}) {
  const res = await API().get("/api/mobile/hazard/open-board", { params });
  return res.data;
}

/** «Би өөрөө шийдлээ» — авсан арга хэмжээ + нотлох зураг. */
export async function selfFixHazard(id, action, afterPhotos) {
  const res = await API().post("/api/mobile/hazard/" + id + "/self-fix", {
    action,
    after_photos: afterPhotos || [],
  });
  return res.data;
}

/**
 * Өөрөө шийдсэн бүртгэлийн жагсаалт.
 *
 * params.state: "pending" (анхдагч) · "approved" · "rejected"
 * Удирдлага бүгдийг, бусад ажилтан зөвхөн ӨӨРИЙН илгээсэнийг харна.
 */
export async function getVerifyQueue(params = {}) {
  const res = await API().get("/api/mobile/hazard/verify-queue", { params });
  return res.data;
}

/**
 * Удирдлагын шийдвэр: approve=true → хаана, false → буцаана.
 * `note` — батлахад САНАЛ (заавал биш), буцаахад ШАЛТГААН (заавал).
 */
export async function verifyHazardFix(id, approve, note) {
  const res = await API().post("/api/mobile/hazard/" + id + "/verify", {
    approve,
    note,
  });
  return res.data;
}

/**
 * Шийдвэрлэсэн бүртгэлийн САНАЛЫГ засах.
 *
 * ⚠ Зөвхөн тайлбар засагдана — шийдвэр өөрөө биш. Эрхийг сервер шалгана
 *   (шийдвэрийг гаргасан хүн эсвэл ХАБЭА).
 */
export async function updateVerifyNote(id, note) {
  const res = await API().post("/api/mobile/hazard/" + id + "/verify-note", {
    note,
  });
  return res.data;
}

// ---- Дуу хоолой → текст (Google STT) ----
export async function transcribeSpeech(blob) {
  const form = new FormData();
  // Файлын нэр нь бодит форматтай таарах ёстой — MediaRecorder нь webm/opus
  // бичдэг. Өмнө нь "voice.wav" гэж илгээдэг байсан тул backend хөрвүүлэлгүй
  // дамжуулж, Chimege таньж чаддаггүй байв.
  const ext = (blob.type || "").includes("ogg") ? "ogg" : "webm";
  form.append("audio", blob, `voice.${ext}`);
  form.append("language_code", "mn-MN");
  const res = await API().post("/api/mobile/hazard/speech", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return res.data; // { transcript, confidence, language_code, ... }
}

// ---- Зураг upload (multipart) ----
export async function uploadHazardPhoto(file) {
  const form = new FormData();
  form.append("file", file);
  const res = await API().post("/api/mobile/hazard/photo", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return res.data;
}

/**
 * Нэвтэрсэн ажилтны БҮХ ӨЭА/наряд бүртгэл — жагсаалтаар.
 *
 * `getMyOeaRegistration` нь зөвхөн ХАМГИЙН СҮҮЛИЙН нэгийг (arr[0]) буцаадаг
 * бөгөөд 5А/15А хуудсууд түүнийг ашигладаг. Харин /me дээр ажилтан өөрийн
 * бүх ажлаа хараад аль нэг дээр нь урьдчилсан шалгалтаа бөглөх шаардлагатай
 * тул бүтэн жагсаалт хэрэгтэй.
 *
 * ⚠ Backend нь `mine=1` үед ТН-ийн тэргүүн тэгийг (017824 / 17824) өөрөө
 *   зохицуулдаг тул `tn` параметрийг ДАМЖУУЛАХГҮЙ — тэр нь яг тэнцүүгээр
 *   шүүдэг учир формат зөрвөл хоосон буцаана.
 */
export async function listMyOeaRegistrations(params = {}) {
  const res = await API().get("/api/mobile/hazard/registertn", {
    params: { mine: true, ...params },
  });
  return Array.isArray(res.data) ? res.data : [];
}

// ---- Зөрчлийн ЛАВЛАХ САН (заалт · арга хэмжээ · төрөл · шийтгэл) ----
// kind: "clause" | "measure" | "type" | "penalty"
export async function listViolationRefs(kind) {
  const res = await API().get("/api/mobile/violation-reference", {
    params: kind ? { kind } : {},
  });
  return res.data;
}

export async function createViolationRef(payload) {
  const res = await API().post("/api/mobile/violation-reference", payload);
  return res.data;
}

export async function updateViolationRef(payload) {
  const res = await API().put("/api/mobile/violation-reference", payload);
  return res.data;
}

export async function deleteViolationRef(id) {
  const res = await API().delete("/api/mobile/violation-reference", {
    params: { id },
  });
  return res.data;
}

// ---- Зөрчлийн ГҮЙЦЭТГЭЛ / ХЯНАЛТЫН бүртгэл ----
// Хуучин зөрчлийн бүртгэлийн `Inspection.Execution` + `Inspection.Approve`
// хоёрын орлуулга: гүйцэтгэлийн хувь, олон удаагийн бичилт, хянасан
// түүх, «дахин гүйцэтгэх» буцаалт.
export async function listHazardExecution(reportId) {
  const res = await API().get("/api/mobile/hazard/execution", {
    params: { report_id: reportId },
  });
  return res.data;
}

export async function addHazardExecution(payload) {
  const res = await API().post("/api/mobile/hazard/execution", payload);
  return res.data;
}

export async function deleteHazardExecution(id) {
  const res = await API().delete("/api/mobile/hazard/execution", {
    params: { id },
  });
  return res.data;
}

// ---- ӨЭА (Өндөр Эрсдэлт Ажилд) бүртгэл шалгах ----
// mine=true: одоогийн нэвтэрсэн хэрэглэгчийн тухайн өдрийн бүртгэлийг буцаана.
//
// ⚠ localStorage FALLBACK ХАСАГДСАН. Өмнө нь бааз хоосон/унасан үед
//   браузерын сангаас уншдаг байв. Энэ функцээр «би өнөөдөр ӨЭА-д
//   бүртгэгдсэн үү» гэдгийг тогтоодог — локал хариу нь ажилтныг
//   БҮРТГЭГДСЭН гэж үзүүлж, 15А бөглүүлээд, гэтэл АШУА болон ажилд
//   гаргах жагсаалтад тэр хүн ХАРАГДАХГҮЙ. Худал «тийм» нь
//   «мэдэхгүй»-ээс аюултай.
export async function getMyOeaRegistration(params = {}) {
  const res = await API().get("/api/mobile/hazard/registertn", {
    params: { mine: true, ...params },
  });
  const arr = Array.isArray(res.data) ? res.data : [];
  return arr.length > 0 ? arr[0] : null;
}

// ---- POST: АШУА ажилтанг ӨЭА-н нарядаар бүртгэх (best-effort) ----
export async function createOeaRegistration(payload) {
  const res = await API().post("/api/mobile/hazard/registertn", payload);
  return res.data;
}

// ---- Ажлын даалгаврын ажил (DB) — олон browser-аас хандах боломжтой ----
export async function listNaryadJobsApi(params = {}) {
  try {
    const res = await API().get("/api/mobile/naryad/jobs", { params });
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function createNaryadJobApi(payload) {
  const res = await API().post("/api/mobile/naryad/jobs", payload);
  return res.data;
}

export async function updateNaryadJobApi(id, payload) {
  const res = await API().put(`/api/mobile/naryad/jobs/${id}`, payload);
  return res.data;
}

export async function deleteNaryadJobApi(id) {
  const res = await API().delete(`/api/mobile/naryad/jobs/${id}`);
  return res.data;
}

// ---- Дуу хоолойн файл upload (MinIO → hazard/voice/...) ----
export async function uploadHazardVoice(blob) {
  const form = new FormData();
  form.append("file", blob, "voice.webm");
  const res = await API().post("/api/mobile/hazard/photo", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return res.data; // { file_url, file_size, ... }
}

// ---- ӨЭА/наряд бүртгэлийг шинэчлэх (урьдчилсан шалгалт илгээх) ----
// checklist_5a_completed / checklist_15_completed илгээхэд backend нь
// work_approval_status = "pending" болгож, анхан шатны удирдах ажилтны
// шийдвэрийг хүлээнэ.
export async function updateOeaRegistration(id, payload) {
  const res = await API().put("/api/mobile/hazard/registertn/" + id, payload);
  return res.data;
}

// ---- Ажилд гаргах зөвшөөрөл — анхан шатны удирдах ажилтны шийдвэр ----
// payload: { status: "approved" | "rejected", note }
export async function decideOeaWorkApproval(id, payload) {
  const res = await API().post(
    "/api/mobile/hazard/registertn/" + id + "/work-approval",
    payload,
  );
  return res.data;
}

// ---- ӨЭА/наряд бүртгэлийн жагсаалт (DB) — огнооны мужаар ----
// /highrisk/workapproval хуудас ашиглана (localStorage биш, жинхэнэ бааз).
export async function listOeaRegistrations(params = {}) {
  const res = await API().get("/api/mobile/hazard/registertn", { params });
  return Array.isArray(res.data) ? res.data : [];
}

// ---- ӨЭА/наряд бүртгэлийг устгах (soft delete) ----
export async function deleteOeaRegistration(id) {
  const res = await API().delete("/api/mobile/hazard/registertn/" + id);
  return res.data;
}

// ============================================================================
// Урьдчилсан шалгалтын бөглөлт (5А / 15А) — xab.prework_checklist_submission
//
// ДҮРЭМ: ажлын ДААЛГАВАР (NARYAD) → 5A · наряд ЗӨВШӨӨРӨЛ = ӨЭА (OEA) → CRITICAL
// Мөр бүр = НЭГ хүний хийсэн НЭГ шалгалт (checker_role: WORKER|ASHUA|HSE).
// АШУА нь ажилтны ӨМНӨӨС бөглөхгүй — ӨЭА ажил дээр очиж ӨӨРИЙН 15А-гаа
// бөглөхөд checker_role="ASHUA" гэж илгээнэ.
// ============================================================================

/**
 * Бөглөлт илгээх.
 * payload: {
 *   work_type: "OEA"|"NARYAD", naryad_job_id, oea_permit_id,
 *   oea_registration_id, naryad_worker_id, work_date,
 *   checker_role: "WORKER"|"ASHUA"|"HSE",
 *   tn, worker_name, position,
 *   department_code/name, section_code/name, destination,
 *   gps: {lat,lng,accuracy}, started_at, source, offline_client_id,
 *   answers: [{ checklist_item_id|item_code, item_no, item_title,
 *               answer:"YES"|"NO"|"NA", hazard_report_id, note,
 *               photo_url, voice_file_url }]
 * }
 * Дүн (issue/hazard/score/work_stopped) серверт бодогдоно — илгээх шаардлагагүй.
 */
export async function createPreworkSubmission(payload) {
  const res = await API().post(
    "/api/mobile/hazard/prework-submission",
    payload,
  );
  return res.data;
}

/** Жагсаалт — ажлын дугаар / шалгагчийн үүрэг / огноогоор шүүнэ. */
export async function listPreworkSubmissions(params) {
  const res = await API().get("/api/mobile/hazard/prework-submission", {
    params,
  });
  return res.data;
}

/** Дэлгэрэнгүй — асуумж бүрийн хариулттай. */
export async function getPreworkSubmission(id) {
  const res = await API().get("/api/mobile/hazard/prework-submission/" + id);
  return res.data;
}

/**
 * Бөглөх шаардлагын төлөв.
 * Буцаах: { has_work, work_type, naryad_job_id, oea_permit_id,
 *           oea_registration_id, required_checklist: "5A"|"CRITICAL",
 *           my_submission, ashua_required, ashua_done }
 */
export async function getPreworkStatus(params) {
  const res = await API().get(
    "/api/mobile/hazard/prework-submission/status",
    { params },
  );
  return res.data;
}
